import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getPost, getRelated } from "@/lib/blog";
import { waLink, WA_MESSAGES } from "@/lib/site";
import { Reveal, ArrowButton } from "@/components/landing/Reveal";

const BlogPost = () => {
    const { slug } = useParams();
    const post = getPost(slug);

    useEffect(() => {
        if (post) {
            document.title = `${post.title} | BookMyVIP`;
            document
                .querySelector('meta[name="description"]')
                ?.setAttribute("content", post.meta);
        }
        return () => {
            document.title = "BookMyVIP | VIP Table Booking in Thailand";
        };
    }, [post]);

    if (!post) return <Navigate to="/blog" replace />;

    const related = getRelated(post);

    return (
        <main data-testid={`blog-post-${post.slug}`}>
            <article className="mx-auto max-w-3xl px-5 pb-24 pt-32 md:px-10 md:pt-40">
                <Reveal>
                    <Link
                        data-testid="blog-back-link"
                        to="/blog"
                        className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-muted transition-colors duration-300 hover:text-gold"
                    >
                        <ArrowLeft size={14} strokeWidth={1.5} /> The Journal
                    </Link>
                </Reveal>
                <Reveal delay={0.05}>
                    <div className="mt-10 flex items-center gap-4">
                        <span className="border border-gold/40 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.25em] text-gold">
                            {post.category}
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">
                            {post.readTime}
                        </span>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1 className="mt-8 font-serif text-3xl font-medium leading-tight tracking-tight text-ivory md:text-5xl">
                        {post.title}
                    </h1>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="relative mt-12 h-[46svh] overflow-hidden rounded-sm border border-white/10">
                        <img
                            src={post.image}
                            alt={post.title}
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                </Reveal>

                <div className="mt-14 space-y-12">
                    {post.sections.map((s, i) => (
                        <Reveal key={s.h} delay={0.03}>
                            <section className="border-t border-white/10 pt-8">
                                <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-gold">
                                    {String(i + 1).padStart(2, "0")} — {s.h}
                                </h2>
                                <p className="mt-4 text-sm leading-relaxed text-muted md:text-base">
                                    {s.b}
                                </p>
                            </section>
                        </Reveal>
                    ))}
                </div>

                <Reveal>
                    <div className="mt-16 rounded-sm border border-gold/30 bg-[#0F0F0F] p-8 md:p-10">
                        <p className="font-serif text-2xl italic text-ivory md:text-3xl">
                            Planning this night?{" "}
                            <span className="text-gold">We'll arrange it.</span>
                        </p>
                        <p className="mt-4 text-sm leading-relaxed text-muted">
                            Tell our concierge your destination, date and group
                            size — we'll share the VIP table options that fit.
                        </p>
                        <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                            <ArrowButton
                                testId="blog-cta-whatsapp-btn"
                                href={waLink(WA_MESSAGES.concierge)}
                            >
                                WhatsApp Concierge
                            </ArrowButton>
                            <Link
                                data-testid="blog-cta-quiz-btn"
                                to="/plan"
                                className="inline-flex items-center justify-center gap-3 rounded-sm border border-white/25 px-8 py-4 text-[11px] font-bold uppercase tracking-[0.25em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
                            >
                                Build My Night →
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </article>

            <section className="border-t border-white/10 bg-[#0A0A0A]">
                <div className="mx-auto max-w-7xl px-5 py-20 md:px-10">
                    <Reveal>
                        <h2 className="font-serif text-2xl font-medium tracking-tight text-ivory md:text-3xl">
                            KEEP <span className="italic text-gold">READING.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-6 md:grid-cols-3">
                        {related.map((p) => (
                            <Reveal key={p.slug} className="h-full">
                                <Link
                                    data-testid={`blog-related-${p.slug}`}
                                    to={`/blog/${p.slug}`}
                                    className="group flex h-full flex-col rounded-sm border border-white/10 bg-[#0F0F0F] p-7 transition-colors duration-500 hover:border-gold/40"
                                >
                                    <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-gold">
                                        {p.category}
                                    </span>
                                    <span className="mt-4 flex-1 font-serif text-xl leading-snug text-ivory transition-colors duration-300 group-hover:text-gold">
                                        {p.title}
                                    </span>
                                    <span className="mt-6 text-[10px] font-bold uppercase tracking-[0.25em] text-ivory/50">
                                        {p.readTime}
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default BlogPost;
